
import { useState, useEffect } from "react";
import logolight from "../../assets/Netraa Logo Transparent.png";
import logodark from "../../assets/Netraa Logo Dark.png";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { FiSun, FiMoon } from "react-icons/fi";
import { MdVerified, MdLockPerson, MdVerifiedUser, MdArrowForward } from "react-icons/md";
import logofooter from "../../assets/Apmosys Products Transparent logo.png"

import {
  FiActivity,
  FiCheckCircle,
  FiServer,
  FiCode,
  FiTrendingUp,
  FiEye,
  FiAlertTriangle,
  FiList,
  FiBarChart2,
  FiTool,
  FiFileText,
  FiTarget,
} from "react-icons/fi";

const useCases = [
  {
    title: "Continuous Validation of Business Critical User Journeys",
    desc: "Continuously validate critical user workflows such as login, navigation, and transactions before real users are impacted.",
    icon: <FiCheckCircle />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "Organizations often identify workflow failures only after users experience them, resulting in operational disruption.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "The platform executes configured multi step monitors such as login, navigation, and workflow validation at scheduled intervals.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Critical workflows are continuously validated, helping teams identify issues before they escalate.",
      },
    ],
  },
  {
    title: "Detecting Application Availability Across Environments",
    desc: "Gain visibility into uptime and service reliability across all monitored environments and services.",
    icon: <FiCode />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "An application may appear operational while certain monitored services experience downtime.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "Availability metrics are collected and presented through KPI dashboards for all active monitors.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Teams gain visibility into uptime performance and can track service reliability effectively.",
      },
    ],
  },
  {
    title: "Monitoring API Response Behavior",
    desc: "Continuously validate API endpoints to detect failures, slow responses, and validation issues early.",
    icon: <FiCode />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "Backend service issues may remain unnoticed without dedicated monitoring.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "Configured API monitors validate endpoint response time and configured validation checks.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "API related issues can be identified early, improving overall service stability.",
      },
    ],
  },
  {
    title: "Identifying Performance Bottlenecks",
    desc: "Analyze detailed timing metrics to isolate slow components and improve application responsiveness.",
    icon: <FiTrendingUp />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "Slow loading pages reduce service quality, but high level metrics may not reveal the source of delays.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "Detailed timing metrics such as Connect Time, First Byte Time, DOM Load Time, and Total Page Load are captured and analyzed.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Teams can isolate performance bottlenecks and improve application responsiveness.",
      },
    ],
  },
  {
    title: "Real Time Visibility for Operations Teams",
    desc: "Track live execution states of all monitors to ensure operational awareness and faster response.",
    icon: <FiEye />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "Without live execution tracking, operational teams may miss critical failures in monitor runs.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "The Live Dashboard provides execution states such as Success, Running, and Terminated for all configured monitors.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Operations teams gain immediate awareness of monitor health and execution status.",
      },
    ],
  },
  {
    title: "Early Identification of Service Disruptions",
    desc: "Quickly detect failures and disruptions with structured error visibility and incident tracking.",
    icon: <FiAlertTriangle />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "A sudden increase in failures can affect service quality if not observed promptly.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "The Errors Overview dashboard provides structured visibility into incidents, including timing, descriptions, and current status.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Teams can review and address disruptions with clear operational context.",
      },
    ],
  },
  {
    title: "Categorizing Failures for Faster Investigation",
    desc: "Organize incidents into structured records to simplify troubleshooting and root cause analysis.",
    icon: <FiList />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "When issues arise, identifying the nature of the failure can delay corrective action.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "The platform organizes incident details into structured error records for easier analysis.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Teams can investigate failures more efficiently and reduce troubleshooting effort.",
      },
    ],
  },
  {
    title: "Quantifying User Experience Through Performance Metrics",
    desc: "Measure real user experience through detailed KPI dashboards and performance analytics.",
    icon: <FiBarChart2 />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "Average values alone may not provide sufficient insight into overall experience quality.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "The KPI Dashboard and performance analytics present timing data and trends for monitored workflows.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Teams can evaluate service quality with deeper operational visibility.",
      },
    ],
  },
  {
    title: "Preserving Reporting Accuracy During Planned Maintenance",
    desc: "Exclude maintenance windows to ensure uptime and performance reports reflect real conditions.",
    icon: <FiTool />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "Scheduled maintenance windows can negatively affect uptime reports if not excluded properly.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "Maintenance Exception Management allows approved time windows to be excluded from reporting calculations.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Reports remain aligned with actual operational conditions.",
      },
    ],
  },
  {
    title: "Automating Performance Reporting for Stakeholders",
    desc: "Generate consistent and scheduled reports automatically for stakeholders.",
    icon: <FiFileText />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "Manual report preparation is time consuming and inconsistent.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "The reporting engine supports scheduled generation of single and bulk reports.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Stakeholders receive regular performance updates in a standardized format.",
      },
    ],
  },
  {
    title: "Prioritizing Optimization with Performance Rankings",
    desc: "Identify slow performing pages and prioritize optimization efforts effectively.",
    icon: <FiTarget />,
    subItems: [
      {
        num: "1",
        title: "Problem Statement",
        desc: "Teams may struggle to decide which pages require immediate improvement.",
      },
      {
        num: "2",
        title: "Solution by Netraa LH",
        desc: "The platform dynamically identifies top slow performing pages for focused review.",
      },
      {
        num: "3",
        title: "Outcome",
        desc: "Optimization efforts are directed toward measurable performance issues.",
      },
    ],
  },
];

export default function UseCases1() {
  const [darkMode, setDarkMode] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // index of open use case
  const navigate = useNavigate();

  useEffect(() => {
    const theme = localStorage.getItem("theme") || "light";
    setDarkMode(theme === "dark");
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = activeModal !== null ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [activeModal]);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const openModal = (i) => setActiveModal(i);
  const closeModal = () => setActiveModal(null);

  const modalData = activeModal !== null ? useCases[activeModal] : null;

  return (
    <div className="bg-white dark:bg-[#141414] text-gray-900 dark:text-gray-100 transition-colors duration-300">

      {/* ── HEADER ── */}
      <header className="header bg-white dark:bg-[#141414] border-b border-gray-200 dark:border-gray-800 px-2 lg:px-6">
        <div className="flex items-center justify-between h-full">

          {/* LEFT: LOGO */}
          <div className="flex items-center gap-3 w-64">
            <div className="flex items-center justify-start -ml-4">
              <img src={logolight} alt="Netraa Docs Light" className="h-24 mt-3 w-auto dark:hidden" />
              <img src={logodark}  alt="Netraa Docs Dark"  className="h-24 mt-3 w-auto hidden dark:block" />
            </div>
          </div>

          {/* RIGHT: THEME + BACK */}
          <div className="flex items-center gap-6 w-64 justify-end">
            <button
              onClick={toggleTheme}
              aria-label="Toggle Dark Mode"
              className="relative flex items-center w-8 h-6 sm:w-12 sm:h-7
                         rounded-full bg-gray-300 dark:bg-orange-500
                         transition px-1 overflow-hidden"
            >
              <span className="w-4 h-4 sm:w-5 sm:h-5 bg-white rounded-full shadow-md
                               transform transition-transform duration-300
                               flex items-center justify-center
                               translate-x-0 dark:translate-x-2 sm:dark:translate-x-5">
                <FiSun  className="text-orange-500 text-[10px] sm:text-xs dark:hidden" />
                <FiMoon className="text-gray-700  text-[10px] sm:text-xs hidden dark:block" />
              </span>
            </button>

            <div className="relative group">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition"
              >
                <ArrowLeftIcon className="w-6 h-6" />
              </button>
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1
                               px-2 py-1 text-xs rounded bg-gray-800 text-white
                               opacity-0 group-hover:opacity-100 transition
                               pointer-events-none whitespace-nowrap">
                Back
              </span>
            </div>
          </div>
        </div>
      </header>

      <div>

        {/* ── HERO ── */}
        <section className="mx-auto px-10 pt-20 pb-14 flex flex-col items-center text-center">
          <h1 className="text-6xl font-extrabold text-black dark:text-white leading-tight mb-5">
            The Most Popular Use Cases
            <br />
            of{" "}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
              Netraa LH
            </span>
          </h1>
          <p className="text-[15px] text-gray-500 dark:text-gray-400 leading-relaxed">
            Explore a wide range of powerful use cases that can be automated and streamlined with Netraa LH.
          </p>
        </section>

        {/* ── OVERVIEW ── */}
<section className="mx-auto px-10 pb-16">
  <div className="flex items-center gap-4 mb-10">
    <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
    <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 dark:text-gray-500 px-1">
      Overview
    </span>
    <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
  </div>

  <div className="flex flex-col lg:flex-row gap-12 mb-10">
    <div className="lg:w-52 flex-shrink-0 flex flex-row gap-4">
      <div className="w-1 h-14 bg-orange-500 rounded-full" />
      <h2
        className="text-[22px] font-extrabold text-gray-900 dark:text-gray-100 leading-snug"
      >
        What is<br />
        <span className="text-orange-500">Netraa LH?</span>
      </h2>
    </div>

    <div className="flex-1 flex flex-col gap-4">
      <p className="text-[15px] text-gray-600 dark:text-gray-400 leading-[1.85]">
        <span className="font-semibold text-gray-900 dark:text-white">Netraa LH</span> is a proactive synthetic intelligence platform built to continuously validate digital experiences before real users are affected.
                Unlike traditional monitoring systems that focus primarily on server health, Netraa LH measures whether actual business transactions and user journeys are functioning as expected.
        It executes configured monitors at scheduled intervals and captures operational data such as response time, availability, downtime, and error behaviour.

      </p>

      <p className="text-[15px] text-gray-600 dark:text-gray-400 leading-[1.85]">
        The platform combines dashboards, performance analytics, error intelligence, reporting workflows, and benchmarking capabilities into a unified operational ecosystem.
        Its purpose is to provide organizations with visibility into application reliability, service quality, and performance trends so that issues can be identified and addressed in a timely manner.

      </p>
    </div>
  </div>

  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    {[
      {
        label: "Continuous Validation",
        desc: "Executes multi-step synthetic monitors for workflows",
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        ),
      },
      {
        label: "Availability Monitoring",
        desc: "Tracks uptime across environments and services",
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
          </svg>
        ),
      },
      {
        label: "API Monitoring",
        desc: "Validates endpoint response behavior and stability",
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6" />
            <polyline points="8 6 2 12 8 18" />
          </svg>
        ),
      },
      {
        label: "Performance Insights",
        desc: "Captures load time, and bottleneck metrics",
        icon: (
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        ),
      },
    ].map(({ label, desc, icon }) => (
      <div
        key={label}
        className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-[#1a1a1a] hover:border-orange-300 dark:hover:border-orange-800 transition-colors duration-200 group"
      >
        <div className="w-8 h-8 rounded-lg bg-white dark:bg-[#222] border border-gray-100 dark:border-gray-700 flex items-center justify-center text-orange-500 flex-shrink-0 shadow-sm group-hover:border-orange-200 dark:group-hover:border-orange-800 transition-colors">
          {icon}
        </div>
        <div>
          <p
            className="text-[13px] font-bold text-gray-800 dark:text-gray-200 mb-0.5"
          >
            {label}
          </p>
          <p className="text-[11.5px] text-gray-500 dark:text-gray-400 leading-snug">
            {desc}
          </p>
        </div>
      </div>
    ))}
  </div>
</section>

        {/* ── USE CASE CARDS ── */}
        <section className="mx-auto px-10 pb-20">
         <div className="mb-10">
  {/* Top divider + label */}
  <div className="flex items-center gap-4 mb-3">
    <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
    <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 dark:text-gray-500 px-1">
      Use Cases
    </span>
    <div className="h-px flex-1 bg-gray-100 dark:bg-gray-800" />
  </div>

  {/* Description */}
  <p className="text-[12px] text-gray-500 dark:text-gray-400 text-center max-w-lg mx-auto leading-relaxed">
    Each use case highlights a key capability of Netraa LH — covering real-world problems, how the platform addresses them, and the outcomes delivered. Click any card to explore more.
  </p>
</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {useCases.map((card, i) => (
              <div
                key={card.title}
                onMouseEnter={() => setHoveredCard(i)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => openModal(i)}
                className={`
                  rounded-2xl p-7 border cursor-default
                  bg-white dark:bg-[#1e1e1e]
                  transition-all duration-300 flex flex-col
                  ${hoveredCard === i
                    ? "border-orange-500 shadow-[0_8px_36px_rgba(249,115,22,0.15)] -translate-y-1"
                    : "border-gray-100 dark:border-[#2a2a2a]"
                  }
                `}
              >
                {/* Icon + badge row */}
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-xl bg-orange-50 dark:bg-[#2a1a0a] flex items-center justify-center text-orange-500 flex-shrink-0">
                    <span className="w-5 h-5 flex">{card.icon}</span>
                  </div>
                  <span className="text-[11px] font-semibold text-orange-500 bg-orange-50 dark:bg-orange-500/10 px-2 py-1 rounded-full">
                    {card.subItems.length} sub-topics
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-[15px] font-bold text-gray-900 dark:text-gray-100 mb-2 leading-snug"
                    >
                  {card.title}
                </h3>

                {/* Description */}
                <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed mb-5 flex-1">
                  {card.desc}
                </p>

                {/* Sub-item previews */}
                {/* <ul className="space-y-1.5 mb-5">
                  {card.subItems.map((sub) => (
                    <li key={sub.num} className="flex items-center gap-2 text-[12px] text-gray-500 dark:text-gray-400">
                      <span className="w-5 h-5 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-500 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                        {sub.num.split(".")[1]}
                      </span>
                      {sub.title}
                    </li>
                  ))}
                </ul> */}

                {/* Read more */}
                <button
                  onClick={() => openModal(i)}
                  className="text-[13px] font-semibold text-orange-500 hover:text-orange-600
                             inline-flex items-center gap-1 group transition-colors self-start"
                >
                  Read more
                  <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                </button>
              </div>
            ))}
          </div>
        </section>
        {/* ── REAL WORLD SCENARIOS ── */}
<section className="relative mx-auto px-10 pb-2">
  <div className="absolute inset-0 -z-10 opacity-[0.03]
    bg-[radial-gradient(circle_at_1px_1px,#000_1px,transparent_0)]
    [background-size:20px_20px]" />

  <div className="flex flex-col items-center mb-16 text-center">
    <span className="text-xs font-semibold tracking-[0.2em] uppercase text-orange-500 mb-3">
      Real World Scenarios
    </span>
    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 dark:text-white">
      How Netraa solves real production issues
    </h2>
    <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 max-w-xl">
      See how Netraa LH helps validate workflows, monitor availability, analyze API behavior,
      identify performance bottlenecks, and improve operational visibility in real environments.
    </p>
  </div>

  <div className="space-y-10">
    {[
      {
        title: "Login Workflow Failure During Peak Banking Hours",
        desc: "Customers are unable to complete login and authentication workflows during high traffic periods.",
        without: [
          "Login failures identified only after customer complaints",
          "No continuous validation of authentication workflows",
          "Operational teams lack early visibility into disruptions",
        ],
        with: [
          "Executes scheduled synthetic login validations",
          "Detects workflow failures before large scale impact",
          "Provides real time visibility into monitor execution status",
        ],
        impact: [
          "Reduced customer login disruptions",
          "Faster operational response",
          "Improved digital banking reliability",
        ],
      },
      {
        title: "E-Commerce Checkout Journey Breaking After Deployment",
        desc: "Customers abandon purchases because checkout workflows fail after a production release.",
        without: [
          "Broken checkout flows remain unnoticed initially",
          "Manual validation delays issue identification",
          "Revenue impact due to failed transactions",
        ],
        with: [
          "Continuously validates checkout user journeys",
          "Detects workflow failures through synthetic monitoring",
          "Provides structured operational insights for quick action",
        ],
        impact: [
          "Reduced checkout failures",
          "Improved customer purchase experience",
          "Protection against revenue loss",
        ],
      },
      {
        title: "API Endpoint Instability Affecting Business Services",
        desc: "Backend APIs respond slowly or fail intermittently, impacting dependent business workflows.",
        without: [
          "API instability detected only after service degradation",
          "Limited visibility into endpoint response behavior",
          "Delayed troubleshooting for operations teams",
        ],
        with: [
          "Continuously validates API response behavior",
          "Tracks response time and validation failures",
          "Provides API monitoring insights across environments",
        ],
        impact: [
          "Improved API service reliability",
          "Faster identification of unstable endpoints",
          "Reduced operational disruptions",
        ],
      },
      {
        title: "Slow Loading Customer Portal During Peak Usage",
        desc: "Users experience delays while accessing dashboards and navigating critical application pages.",
        without: [
          "Slow pages difficult to identify proactively",
          "Limited insight into frontend performance degradation",
          "Poor digital experience during peak hours",
        ],
        with: [
          "Captures detailed performance timing metrics",
          "Identifies bottlenecks across monitored workflows",
          "Provides performance benchmarking visibility",
        ],
        impact: [
          "Improved application responsiveness",
          "Better customer experience",
          "Focused performance optimization efforts",
        ],
      },
      {
        title: "Service Downtime Remaining Undetected Across Environments",
        desc: "Certain application services become unavailable while the overall application appears operational.",
        without: [
          "Partial downtime remains unnoticed initially",
          "No centralized uptime visibility across environments",
          "Delayed awareness of service interruptions",
        ],
        with: [
          "Tracks availability metrics for configured monitors",
          "Provides uptime visibility across environments and services",
          "Displays monitor execution health through KPI dashboards",
        ],
        impact: [
          "Improved service reliability visibility",
          "Faster detection of downtime events",
          "Reduced operational risk",
        ],
      },
      {
        title: "Operations Teams Missing Critical Monitor Failures",
        desc: "Execution failures in monitoring workflows go unnoticed due to lack of live operational tracking.",
        without: [
          "Teams lack real time monitor execution awareness",
          "Failed monitor runs identified late",
          "Operational response becomes reactive",
        ],
        with: [
          "Provides live dashboard visibility for all monitor executions",
          "Displays execution states such as Success, Running, and Terminated",
          "Improves operational awareness through centralized monitoring",
        ],
        impact: [
          "Faster operational response",
          "Improved monitoring visibility",
          "Reduced incident escalation time",
        ],
      },
      {
        title: "Frequent Production Errors Delaying Incident Investigation",
        desc: "Operations teams struggle to investigate recurring production issues efficiently.",
        without: [
          "Incident details remain fragmented across systems",
          "Failure categorization becomes difficult",
          "Longer troubleshooting and analysis cycles",
        ],
        with: [
          "Provides structured error visibility and incident tracking",
          "Categorizes failures into organized operational records",
          "Simplifies investigation workflows for support teams",
        ],
        impact: [
          "Reduced troubleshooting effort",
          "Faster incident investigation",
          "Improved operational efficiency",
        ],
      },
    ].map((s) => (
      <div
        key={s.title}
        className="group rounded-2xl border border-gray-200 dark:border-[#2a2a2a]
          bg-gray-50 dark:bg-[#1e1e1e]/80 p-8 transition-all duration-300
          hover:shadow-[0_10px_50px_rgba(249,115,22,0.15)]"
      >
        <h3 className="text-lg font-bold text-orange-500 mb-2">{s.title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{s.desc}</p>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-[#1e1e1e] border border-red-200/60 dark:border-red-900/40 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-red-400 dark:bg-red-500" />
              <h4 className="font-semibold text-red-500 dark:text-red-400 text-sm">Without Netraa LH</h4>
            </div>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              {s.without.map((x) => (
                <li key={x} className="flex items-start gap-1.5">
                  <span className="text-red-400 mt-0.5">✕</span>
                  {x}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white dark:bg-[#1e1e1e] border border-green-200/60 dark:border-green-900/40 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              <h4 className="font-semibold text-green-600 dark:text-green-400 text-sm">With Netraa LH</h4>
            </div>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              {s.with.map((x) => (
                <li key={x} className="flex items-start gap-1.5">
                  <span className="text-green-500 mt-0.5">✓</span>
                  {x}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white dark:bg-[#1e1e1e] border border-orange-200/60 dark:border-orange-900/40 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
              <h4 className="font-semibold text-orange-500 dark:text-orange-400 text-sm">Business Impact</h4>
            </div>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              {s.impact.map((x) => (
                <li key={x} className="flex items-start gap-1.5">
                  <span className="text-orange-400 mt-0.5">↑</span>
                  {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    ))}
  </div>
</section>

{/* Footer */}
      <footer className="bg-white dark:bg-card-dark border-t border-border-color dark:border-white/10 py-10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex flex-col items-center md:items-start gap-1">
            <p className="text-xs font-bold text-text-muted uppercase tracking-wider">Powered by</p>
            <div className="flex items-center gap-3 w-64">
      <div className="flex items-center justify-start -ml-4"> {/* Shift left */}
        <img
          src={logofooter}
          alt="Apmosys logo"
          className="h-10 w-auto dark:hidden"
        />
        <img
          src={logofooter}
          alt="Apmosys logo"
          className="h-10 w-auto hidden dark:block"
        />
      </div>
    </div>

          </div>
          <div className="text-sm text-text-muted text-center">
            © 2026 ApMoSys Products Private Ltd. All rights reserved.
          </div>
          <div className="flex items-center gap-6 grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all">
            <div className="flex flex-col items-center gap-1 group cursor-pointer">
              <MdLockPerson className="text-3xl text-text-main dark:text-white" />
              <span className="text-[10px] font-bold text-text-main dark:text-white">SOC2 Type II</span>
            </div>
            <div className="w-px h-8 bg-border-color dark:bg-white/20"></div>
            <div className="flex flex-col items-center gap-1 group cursor-pointer">
              <MdVerified className="text-3xl text-text-main dark:text-white" />
              <span className="text-[10px] font-bold text-text-main dark:text-white">ISO 27001</span>
            </div>
          </div>
        </div>
      </footer>


      </div>

      {/* ── MODAL ── */}
      {modalData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

          {/* Panel */}
          <div
            className="relative z-10 w-full max-w-2xl max-h-[85vh] overflow-y-auto hide-scrollbar
                        bg-white dark:bg-[#1e1e1e] rounded-2xl shadow-2xl
                        border border-gray-100 dark:border-[#2a2a2a] p-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={closeModal}
              className="absolute top-5 right-5 p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                         hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            {/* Modal header */}
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-orange-50 dark:bg-[#2a1a0a] flex items-center justify-center text-orange-500 flex-shrink-0">
                <span className="w-6 h-6 flex">{modalData.icon}</span>
              </div>
              <div>
                <h2 className="text-[18px] font-extrabold text-gray-900 dark:text-gray-100 leading-snug"
                    style={{ fontFamily: "'Sora', sans-serif" }}>
                  {modalData.title}
                </h2>
                <p className="text-[13px] text-gray-500 dark:text-gray-400 mt-1">{modalData.desc}</p>
              </div>
            </div>

            {/* Sub-items */}
            <div className="space-y-5">
              {modalData.subItems.map((sub, idx) => (
                <div key={sub.num}
                  className="flex gap-4 p-5 rounded-xl border border-gray-100 dark:border-[#2a2a2a] bg-gray-50 dark:bg-[#252525]">
                  {/* Number badge */}
                  <div className="w-8 h-8 rounded-full bg-orange-500 text-white text-[12px] font-bold
                                  flex items-center justify-center flex-shrink-0 mt-0.5">
                    {idx + 1}
                  </div>
                  <div>
                    <p className="text-[14px] font-bold text-gray-900 dark:text-gray-100 mb-1"
                       style={{ fontFamily: "'Sora', sans-serif" }}>
                      {sub.num} {sub.title}
                    </p>
                    <p className="text-[13px] text-gray-500 dark:text-gray-400 leading-relaxed">
                      {sub.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}